import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { reportsService, WorkLogQualityCategory } from 'api';
import type { SmartDetailItem } from 'api';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type DataTableColumn } from 'shared/components/DataTable/DataTable';
import { queryKeys } from 'shared/constants/queryKeys';
import { ROUTE_PATHS } from 'shared/constants/routes';

const CATEGORY_META: Record<
  WorkLogQualityCategory,
  { label: string; color: 'success' | 'warning' | 'error' }
> = {
  [WorkLogQualityCategory.Good]: { label: 'Good', color: 'success' },
  [WorkLogQualityCategory.NeedsReview]: { label: 'Needs review', color: 'warning' },
  [WorkLogQualityCategory.Bad]: { label: 'Bad', color: 'error' },
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const SmartReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const from = searchParams.get('from') || dayjs().startOf('month').format('YYYY-MM-DD');
  const to = searchParams.get('to') || dayjs().endOf('month').format('YYYY-MM-DD');

  const [category, setCategory] = useState<WorkLogQualityCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const summaryParams = { from, to };
  const { data: summary } = useQuery({
    queryKey: queryKeys.reports.smartSummary(summaryParams),
    queryFn: async () => (await reportsService.getSmartSummary(summaryParams)).data.data,
    refetchInterval: (query) =>
      (query.state.data?.totals.unscored ?? 0) > 0 ? 15000 : false,
  });

  const detailParams = { from, to, category: category === 'all' ? undefined : category };
  const { data: details, isLoading } = useQuery({
    queryKey: queryKeys.reports.smartDetails(detailParams),
    queryFn: async () => (await reportsService.getSmartDetails(detailParams)).data.data,
  });

  const elevated = summary?.elevated ?? details?.elevated ?? false;

  // Reset to first page whenever the filters change.
  useEffect(() => {
    setPage(0);
  }, [category, search, from, to]);

  const searchLc = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    const items = details?.items ?? [];
    if (!searchLc) return items;
    return items.filter((i) => i.userName.toLowerCase().includes(searchLc));
  }, [details, searchLc]);

  const filteredPerUser = useMemo(() => {
    const rows = summary?.perUser ?? [];
    if (!searchLc) return rows;
    return rows.filter((u) => u.userName.toLowerCase().includes(searchLc));
  }, [summary, searchLc]);

  const pagedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const columns: DataTableColumn<SmartDetailItem>[] = [
    {
      id: 'startedAt',
      label: 'Date',
      width: 100,
      render: (row) => dayjs(row.startedAt).format('MMM DD'),
    },
    ...(elevated
      ? [
          {
            id: 'user',
            label: 'Employee',
            width: 180,
            render: (row: SmartDetailItem) => row.userName,
          } as DataTableColumn<SmartDetailItem>,
        ]
      : []),
    {
      id: 'description',
      label: 'Description',
      render: (row) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
          {row.description || <em>(empty)</em>}
        </Typography>
      ),
    },
    {
      id: 'category',
      label: 'Quality',
      width: 130,
      render: (row) =>
        row.category === null ? (
          <Chip label="Pending" size="small" variant="outlined" />
        ) : (
          <Chip
            label={CATEGORY_META[row.category].label}
            size="small"
            color={CATEGORY_META[row.category].color}
            variant="outlined"
          />
        ),
    },
    {
      id: 'reason',
      label: 'Reason',
      render: (row) => (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          {row.reason || '—'}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto', gap: 1.5, p: 0.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <IconButton size="small" onClick={() => navigate(ROUTE_PATHS.REPORTS)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6" fontWeight={700}>
          Smart Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dayjs(from).format('MMM DD, YYYY')} – {dayjs(to).format('MMM DD, YYYY')}
        </Typography>
      </Box>

      {/* Pending banner */}
      {!!summary && summary.totals.unscored > 0 && (
        <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ flexShrink: 0, py: 0 }}>
          AI is still scoring {summary.totals.unscored}{' '}
          {summary.totals.unscored === 1 ? 'entry' : 'entries'}. This view refreshes automatically.
        </Alert>
      )}

      {/* Employee search (elevated roles) */}
      {elevated && (
        <TextField
          size="small"
          placeholder="Search employee…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexShrink: 0, maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* Per-employee breakdown (elevated roles only) */}
      {elevated && filteredPerUser.length > 0 && (
        <Paper variant="outlined" sx={{ flexShrink: 0, p: 1.5, borderRadius: 2, maxHeight: 220, overflow: 'auto' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            By employee
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {filteredPerUser.map((u) => (
              <Box key={u.userId} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 180, fontWeight: 500 }}>
                  {u.userName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main', minWidth: 70 }}>
                  {u.good} good
                </Typography>
                <Typography variant="body2" sx={{ color: 'warning.main', minWidth: 110 }}>
                  {u.needsReview} needs review
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main', minWidth: 60 }}>
                  {u.bad} bad
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                  {u.unscored} pending
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Category filter */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={category}
        onChange={(_e, val) => val !== null && setCategory(val)}
        sx={{ flexShrink: 0 }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value={WorkLogQualityCategory.Good}>Good</ToggleButton>
        <ToggleButton value={WorkLogQualityCategory.NeedsReview}>Needs review</ToggleButton>
        <ToggleButton value={WorkLogQualityCategory.Bad}>Bad</ToggleButton>
      </ToggleButtonGroup>

      {/* Details table + pagination */}
      <Box sx={{ flexShrink: 0 }}>
        <DataTable
          columns={columns}
          rows={pagedItems}
          getRowId={(row) => row.timeEntryId}
          loading={isLoading}
          emptyMessage={isLoading ? 'Loading…' : 'No entries for the selected range and filter.'}
        />
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </Box>
    </Box>
  );
};

export default SmartReportPage;
